import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";

const Recommendation = ({
  icon,
  title,
  body,
}: {
  icon: TopicflowIconType;
  title: string;
  body: string;
}) => (
  <div className="flex items-start gap-4">
    <div className="shrink-0 w-10 h-10 rounded-[10px] bg-gray-100 flex items-center justify-center">
      <TopicflowIcon type={icon} className="w-6 h-6 text-gray-900" />
    </div>
    <div className="flex flex-col gap-0.5">
      <div className="tf-header-base-dark">{title}</div>
      <div className="tf-body-sm-light">{body}</div>
    </div>
  </div>
);

/**
 * Full-page gate shown when a manager opens a direct report's profile that hasn't been onboarded
 * yet (the report isn't in the manager's onboarded_report_ids). It replaces the profile content
 * entirely — rather than floating over it as a dialog — so nothing loads behind it. "Let's talk
 * about X" launches Ask AI running the onboard-direct-report skill; "Skip this setup" just reveals
 * the profile. Either action marks the report onboarded so the gate won't auto-show again (the
 * manager can re-run onboarding anytime).
 */
const ReportOnboardingGate = ({
  report,
  onStart,
  onDismiss,
}: {
  report: { name: string; email?: string | null; avatar?: string | null };
  onStart: () => void;
  onDismiss: () => void;
}) => {
  const label = useLabel();
  const oneonone = label("1-on-1");
  const name = report.name;
  const firstName = name.split(" ")[0];

  return (
    <div className="flex-1 overflow-y-auto bg-[#FCFCFD]">
      <div className="mx-auto flex min-h-full w-full max-w-[550px] flex-col justify-center px-6 py-12">
        <div className="flex flex-col gap-8 rounded-2xl border border-gray-200 bg-white p-9">
          <div className="flex flex-col items-start gap-3 text-left">
            <Avatar user={report} className="h-12 w-12" />
            <div className="flex flex-col gap-1">
              <h2 className="tf-header-2xl">
                Let's prepare to work with {firstName}.
              </h2>
              <div className="tf-body-sm-light">
                We're going to walk you through a small activation process about{" "}
                {firstName} to help get you prepared.
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <Recommendation
              icon="user-circle"
              title={`Tell us about ${name}`}
              body={`Help us get to know ${firstName} so we can provide you with insights and context later on.`}
            />
            <Recommendation
              icon="copy-07"
              title="Import your notes"
              body={`Bring in notes from past ${oneonone}s to reference later — these are shared, so ${firstName} will see them too.`}
            />
            <Recommendation
              icon="book-open-01"
              title={`Prepare for your next ${oneonone}`}
              body="Use our AI to help prepare talking points."
            />
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-2.5">
          <Button
            className="w-full !h-10"
            theme={buttonTheme.primary}
            size={ButtonSize.large}
            onClick={onStart}
          >
            Let's talk about {firstName}
            <TopicflowIcon type="chevronRight" className="h-4 w-4" />
          </Button>
          <Button
            className="w-full"
            theme={buttonTheme.text}
            size={ButtonSize.large}
            onClick={onDismiss}
            text="Skip this setup"
          />
        </div>
      </div>
    </div>
  );
};

export default ReportOnboardingGate;
